/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
import { format } from 'date-fns';
import { Customers, Users } from '@prisma/client';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Customer,
  FileSectionRow,
  Section,
  SectionRow,
} from '../../interfaces';
import { getCleanedPhone } from '../global/helper';
import { CustomerOverview } from '../../types/customers';
import CustomerAppointmentRow from '../../components/customers/CustomerAppointmentRow';
import {
  isAppointmentCompleted,
  isAppointmentInPast,
  isAppointmentPending,
  isAppointmentUpcoming,
} from '../../helpers/appointments';

const constructCustomerName = (
  customer:
    | Customer
    | (Customers & {
        user: Users;
      }),
) => `${customer.user.firstName || ''} ${customer.user.lastName || ''}`.trim();

export const getCustomerDetailsSection = (
  customer:
    | Customer
    | (Customers & {
        user: Users;
      }),
  name: string = 'Customer Details',
) => {
  const customerName = constructCustomerName(customer);
  const customerPhone = getCleanedPhone(customer.user.phoneNumber || '');
  let customerDob = '';
  if (customer.dob) {
    customerDob = new Date(customer.dob).toISOString()?.split('T')[0];
  }
  const customerSection: Section = {
    name,
    rows: [
      {
        type: 'TEXT',
        label: 'Name',
        value: [customerName || ''],
      },
      {
        type: 'TEXT',
        label: 'Email',
        value: [customer.user.email || ''],
      },
      {
        type: 'PHONE',
        label: 'Phone Number',
        value: [customerPhone || ''],
      },
      {
        type: 'TEXT',
        label: 'Birthday',
        value: [
          customerDob
            ? format(new Date(`${customerDob}T00:00:00`), 'MM/dd/yyyy')
            : '',
        ],
      },
      {
        type: 'TEXT',
        label: 'IG Handle',
        value: [customer.user.igHandle || ''],
      },
      'internalNotes' in customer
        ? {
            type: 'TEXT',
            label: 'Internal Notes',
            value: [customer.internalNotes || ''],
          }
        : null,
    ].filter(Boolean) as SectionRow[],
  };

  return customerSection;
};

// Set modal data based on the employee data
export function createModalData(
  customer: CustomerOverview,
  {
    deleteFile,
    fileIdBeingDeleted,
  }: {
    deleteFile?: (fileId: string) => void;
    fileIdBeingDeleted?: string;
  } = {},
) {
  const upcomingAppointments = [...(customer.appointments ?? [])]
    ?.filter(
      appointment =>
        isAppointmentUpcoming(appointment) &&
        !isAppointmentCompleted(appointment),
    )
    .sort(
      (a, b) =>
        new Date(a.startDateTime!).getTime() -
        new Date(b.startDateTime!).getTime(),
    );

  const upcomingAppointmentSection =
    (upcomingAppointments?.length || 0) > 0
      ? ({
          name: 'Upcoming Appointments',
          rows: upcomingAppointments.map(
            appointment =>
              ({
                id: appointment.appointmentId,
                type: 'NODE',
                label: '',
                value: [
                  <CustomerAppointmentRow
                    key={appointment.appointmentId}
                    appointment={appointment}
                  />,
                ],
              }) as SectionRow,
          ),
          noGap: true,
        } as Section)
      : undefined;

  const openRequests = [...(customer.appointments || [])]
    .filter(isAppointmentPending)
    .sort(
      (a, b) =>
        new Date(b.updatedAt || '').getTime() -
        new Date(a.updatedAt || '').getTime(),
    );

  const openRequestsSection =
    (openRequests?.length || 0) > 0
      ? ({
          name: 'Open Requests',
          rows: openRequests.map(
            appointment =>
              ({
                id: appointment.appointmentId,
                type: 'NODE',
                label: '',
                value: [
                  <CustomerAppointmentRow
                    key={appointment.appointmentId}
                    appointment={appointment}
                  />,
                ],
              }) as SectionRow,
          ),
          noGap: true,
        } as Section)
      : undefined;

  const pastAppointments = [...(customer.appointments || [])]
    .filter(
      appointment =>
        isAppointmentInPast(appointment) || isAppointmentCompleted(appointment),
    )
    .sort(
      (a, b) =>
        new Date(b.startDateTime!).getTime() -
        new Date(a.startDateTime!).getTime(),
    );

  const pastAppointmentsSection =
    (pastAppointments?.length || 0) > 0
      ? ({
          name: 'Past Appointments',
          rows: pastAppointments.map(
            appointment =>
              ({
                id: appointment.appointmentId,
                type: 'NODE',
                label: '',
                value: [
                  <CustomerAppointmentRow
                    key={appointment.appointmentId}
                    appointment={appointment}
                  />,
                ],
              }) as SectionRow,
          ),
          noGap: true,
        } as Section)
      : undefined;

  const customerSection = getCustomerDetailsSection(customer);

  // DO NOT CHANGE NAME without changing SideBarSection.tsx
  const transactions = {
    name: 'Customer Transactions',
    rows: [],
    values: {
      customer,
    },
  };

  const filesSection = customer.files?.length
    ? ({
        name: 'Customer Files',
        rows: [
          {
            type: 'FILE',
            label: 'Files',
            value: customer.files.map(file => {
              const isDeleting = fileIdBeingDeleted === file.id;
              return {
                id: file.id,
                url: file.url,
                name: file.name,
                type: file.type.includes('image/') ? 'image' : 'pdf',
                actionComponent: deleteFile ? (
                  <LoadingButton
                    variant="light"
                    onClick={e => {
                      e.stopPropagation();
                      deleteFile(file.id);
                    }}
                    loading={isDeleting}
                    disabled={isDeleting}
                    sx={{
                      boxShadow: theme => theme.shadows[2],
                      bgcolor: theme => theme.palette.lightGrey.main,
                      border: 'none',
                      minWidth: 0,
                      p: 0.5,
                      fontSize: '2rem',
                      borderRadius: '50%',
                    }}>
                    <DeleteIcon
                      color="primary"
                      sx={{
                        opacity: isDeleting ? 0 : 1,
                      }}
                    />
                  </LoadingButton>
                ) : undefined,
                showName: true,
              };
            }),
          } as FileSectionRow,
        ],
      } as Section)
    : null;

  const sections = [
    customerSection,
    filesSection,
    upcomingAppointmentSection,
    openRequestsSection,
    pastAppointmentsSection,
    transactions,
  ].filter(Boolean) as Section[];

  const newModalData = {
    sections,
  };

  return newModalData;
}
